import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSparkles,
  faChevronLeft,
  faChevronRight,
  faEyeSlash,
  faEye,
  faLock,
  faRotateRight,
  faCircleInfo,
} from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import UserContext from "../../../state/UserContext";
import Tooltip from "../../common/Tooltip";
import CoachLoadingState from "./CoachLoadingState";
import CoachPriorityCard from "./CoachPriorityCard";
import aiCoachApi from "../../../api/dashboard/aiCoachApi";
import { LOADING_DURATION_MS } from "./config";
import { localStorageHelper, localStorageKeys } from "../../../helpers";
import { CoachCardStatus, CoachSideBarProps, PriorityCard } from "./types";

type RefreshError = "RATE_LIMITED" | null;

function CoachSideBar({
  isReadOnly,
  answerSetUniqueId,
  isManager,
  participants,
}: CoachSideBarProps) {
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const themeColor = `#${userContext.user.client.accentHexColour}`;

  const employeeId = userContext.user.id;
  const firstSeenKey = localStorageKeys.coachBannerDismissed(
    employeeId,
    answerSetUniqueId,
  );
  const coachHiddenKey = localStorageKeys.coachHidden(
    employeeId,
    answerSetUniqueId,
  );

  const alreadySeen = localStorageHelper.getItem(firstSeenKey) === "true";

  const [coachHidden, setCoachHidden] = useState<boolean>(
    () => localStorageHelper.getItem(coachHiddenKey) === "true",
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [contentVisible, setContentVisible] = useState<boolean>(alreadySeen);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<RefreshError>(null);

  const [cards, setCards] = useState<PriorityCard[]>([]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [showDismissed, setShowDismissed] = useState(false);

  const api = useMemo(
    () => new aiCoachApi(auth.user?.access_token),
    [auth.user?.access_token],
  );

  // ── Initial card load ────────────────────────────────────────────────────

  const loadCards = useCallback(async () => {
    // 1. Try to get existing cards
    const existing = await api.getCards(answerSetUniqueId, isManager);
    if (existing && existing.length > 0) {
      setCards(existing);
      return;
    }

    // 2. No cards yet — auto-generate on first load
    const result = await api.generate(answerSetUniqueId, isManager);
    if (result === "RATE_LIMITED") {
      setRefreshError("RATE_LIMITED");
    } else if (result && result.cards) {
      setCards(result.cards);
    }
  }, [api, answerSetUniqueId, isManager]);

  useEffect(() => {
    const runLoad = async () => {
      await loadCards();
      setIsLoading(false);
      localStorageHelper.setItem(firstSeenKey, "true");
      requestAnimationFrame(() => setContentVisible(true));
    };

    if (!alreadySeen) {
      // Show the AI loading animation for the minimum duration, then fetch
      const timer = setTimeout(runLoad, LOADING_DURATION_MS);
      return () => clearTimeout(timer);
    } else {
      runLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-load when manager/employee role changes
  useEffect(() => {
    setActiveCardIndex(0);
    setShowDismissed(false);
    if (!isLoading) {
      loadCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  // Clamp index when the visible set shrinks (respects showDismissed)
  useEffect(() => {
    const visibleCount = showDismissed
      ? cards.length
      : cards.filter((c) => c.status !== "DISMISSED").length;
    if (visibleCount > 0 && activeCardIndex >= visibleCount) {
      setActiveCardIndex(visibleCount - 1);
    }
  }, [cards, activeCardIndex, showDismissed]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleStatusChange = useCallback(
    (id: number, status: CoachCardStatus) => {
      setCards((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
      api
        .updateCardStatus(id, status as "DISCUSSED" | "DISMISSED")
        .catch(() => {});
    },
    [api],
  );

  const handleCreateFeedback = useCallback(
    async (cardId: number, value: "UP" | "DOWN"): Promise<number | null> => {
      const result = await api.createFeedback(cardId, value);
      return result?.feedbackId ?? null;
    },
    [api],
  );

  const handleUpdateFeedbackText = useCallback(
    async (feedbackId: number, text: string): Promise<void> => {
      await api.updateFeedbackText(feedbackId, text).catch(() => {});
    },
    [api],
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshError(null);

    const result = await api.generate(answerSetUniqueId, isManager);
    if (result === "RATE_LIMITED") {
      setRefreshError("RATE_LIMITED");
    } else if (result && result.cards) {
      setCards(result.cards);
      setActiveCardIndex(0);
      setShowDismissed(false);
    }
    setIsRefreshing(false);
  };

  // Build token map from participants — resolves {{EMPLOYEE_NAME}} etc. in card text
  const tokens: Record<string, string> = participants
    ? {
        EMPLOYEE_NAME: participants.employeeName,
        MANAGER_NAME: participants.managerName,
      }
    : {};

  if (coachHidden) {
    return (
      <div className="hidden lg:block text-center text-gray-400 pt-6 space-y-2">
        <FontAwesomeIcon icon={faEyeSlash} className="text-2xl" />
        <p className="text-sm">Coach is hidden for this journey.</p>
        <button
          onClick={() => {
            setCoachHidden(false);
            localStorageHelper.removeItem(coachHiddenKey);
          }}
          className="text-xs text-blue-600 hover:underline"
        >
          Show coach
        </button>
      </div>
    );
  }

  if (isLoading || isRefreshing) {
    return (
      <div className="hidden lg:block lg:overflow-auto px-2">
        <CoachLoadingState themeColor={themeColor} />
      </div>
    );
  }

  const noCards = cards.length === 0;

  const activePriorityCards = cards.filter((c) => c.status !== "DISMISSED");
  const dismissedCards = cards.filter((c) => c.status === "DISMISSED");
  const dismissedCount = dismissedCards.length;

  const visibleCards = showDismissed
    ? [...activePriorityCards, ...dismissedCards]
    : activePriorityCards;

  const total = visibleCards.length;
  const activeCard = visibleCards[activeCardIndex] ?? null;
  const allDismissed = activePriorityCards.length === 0 && dismissedCount > 0;

  const canGoPrev = activeCardIndex > 0;
  const canGoNext = activeCardIndex < total - 1;

  return (
    <div
      className={cx(
        "hidden lg:flex lg:flex-col lg:overflow-auto transition-opacity duration-500 gap-4 px-2",
        contentVisible ? "opacity-100" : "opacity-0",
      )}
    >
      {/* ── Header: title + refresh + pager ── */}
      <div className="flex items-center gap-2">
        <FontAwesomeIcon
          icon={faSparkles}
          className="shrink-0"
          style={{ color: themeColor }}
        />
        <h3 className="!mb-0 font-semibold text-slate-600">Meeting coach</h3>
        <Tooltip
          content="Regenerate coach guidance"
          appearAt="bottom"
          triggerElement={
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="Refresh coach"
              className={cx(
                "w-6 h-6 flex items-center justify-center rounded transition-colors flex-grow-0",
                isRefreshing
                  ? "text-slate-300 cursor-not-allowed"
                  : "text-slate-400 hover:text-slate-600",
              )}
            >
              <FontAwesomeIcon
                icon={faRotateRight}
                className={cx("text-xs", isRefreshing && "animate-spin")}
              />
            </button>
          }
        />
        <div className="flex-grow" />
        {total > 0 && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              onClick={() => setActiveCardIndex((i) => Math.max(0, i - 1))}
              disabled={!canGoPrev}
              aria-label="Previous priority"
              className={cx(
                "w-5 h-5 flex items-center justify-center rounded transition-colors",
                canGoPrev
                  ? "text-slate-400 hover:text-slate-600"
                  : "text-slate-200 cursor-default",
              )}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="text-[10px]" />
            </button>
            <span className="text-xs text-slate-400 tabular-nums w-8 text-center">
              {activeCardIndex + 1} / {total}
            </span>
            <button
              onClick={() =>
                setActiveCardIndex((i) => Math.min(total - 1, i + 1))
              }
              disabled={!canGoNext}
              aria-label="Next priority"
              className={cx(
                "w-5 h-5 flex items-center justify-center rounded transition-colors",
                canGoNext
                  ? "text-slate-400 hover:text-slate-600"
                  : "text-slate-200 cursor-default",
              )}
            >
              <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
            </button>
          </div>
        )}
      </div>

      {/* ── Rate-limit error ── */}
      {refreshError === "RATE_LIMITED" && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 -mt-2">
          You&apos;ve refreshed recently — please wait a moment before trying
          again.
        </div>
      )}

      {/* ── Intro — hidden when no cards or all dismissed ── */}
      {!noCards && !(allDismissed && !showDismissed) && (
        <p className="text-sm text-slate-500 -mt-2">
          Your top priorities for this conversation, in order:
        </p>
      )}

      {/* ── Active priority card ── */}
      {noCards ? (
        <div className="rounded-md border border-slate-100 bg-slate-50 px-4 py-6 text-center space-y-2">
          <FontAwesomeIcon
            icon={faCircleInfo}
            className="text-slate-300 text-xl"
          />
          <p className="text-sm text-slate-500">
            No recommendations for this conversation.
          </p>
          <p className="text-xs text-slate-400">
            Nothing stood out in the current content. Try refreshing once more
            of the document is completed.
          </p>
        </div>
      ) : allDismissed && !showDismissed ? (
        <div className="rounded-md border border-slate-100 bg-slate-50 px-4 py-6 text-center space-y-2">
          <p className="text-sm text-slate-400">All priorities dismissed.</p>
          <button
            onClick={() => {
              setShowDismissed(true);
              setActiveCardIndex(0);
            }}
            className="text-sm text-blue-600 hover:underline"
          >
            Show dismissed
          </button>
        </div>
      ) : activeCard ? (
        <CoachPriorityCard
          card={activeCard}
          isReadOnly={isReadOnly}
          tokens={tokens}
          onStatusChange={handleStatusChange}
          onCreateFeedback={handleCreateFeedback}
          onUpdateFeedbackText={handleUpdateFeedbackText}
        />
      ) : null}

      {/* ── Below-card navigation ── */}
      {total > 1 && (
        <div className="flex items-center justify-between -mt-1">
          <button
            onClick={() => setActiveCardIndex((i) => Math.max(0, i - 1))}
            disabled={!canGoPrev}
            className={cx(
              "flex items-center gap-1.5 text-sm transition-colors ml-1",
              canGoPrev
                ? "text-slate-500 hover:text-slate-700"
                : "text-slate-200 cursor-default",
            )}
            aria-label="Previous priority"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
            Previous
          </button>
          <button
            onClick={() =>
              setActiveCardIndex((i) => Math.min(total - 1, i + 1))
            }
            disabled={!canGoNext}
            className={cx(
              "flex items-center gap-1.5 text-sm transition-colors mr-1",
              canGoNext
                ? "text-slate-500 hover:text-slate-700"
                : "text-slate-200 cursor-default",
            )}
            aria-label="Next priority"
          >
            Next
            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
          </button>
        </div>
      )}

      {/* ── Dismissed card restoration ── */}
      {dismissedCount > 0 && !allDismissed && (
        <button
          onClick={() => {
            if (showDismissed) {
              setShowDismissed(false);
              const newTotal = activePriorityCards.length;
              if (activeCardIndex >= newTotal) {
                setActiveCardIndex(Math.max(0, newTotal - 1));
              }
            } else {
              setShowDismissed(true);
            }
          }}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors self-start"
        >
          <FontAwesomeIcon icon={showDismissed ? faEyeSlash : faEye} />
          {dismissedCount} dismissed · {showDismissed ? "Hide" : "Show"}
        </button>
      )}

      {/* ── Footer ── */}
      <div className="pt-2 border-t border-slate-100 mt-auto space-y-1.5 text-center text-slate-400">
        <p className="text-xs">AI-assisted. Always use your own judgement.</p>
        <div className="flex justify-center items-center gap-1.5">
          <FontAwesomeIcon
            icon={faLock}
            className="text-slate-300 text-xs shrink-0"
          />
          <span className="text-xs">This guidance is private to you</span>
        </div>
      </div>
    </div>
  );
}

export default CoachSideBar;
